import styled from '@emotion/styled';
import { Alert, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type {
  NpsResult,
  AggregatedResult,
  QuestionResults,
} from '../../../../domain/Surveys/Results/AggregatedResult';
import type { SurveyQuestion } from '../../../../domain/Surveys/Questions/SurveyQuestion';
import { BarChartWithTable } from '../../Results/BarChartWithTable/BarChartWithTable';
import { NPSView } from '../../Results/NPSView/NPSView';
import { ReactionSummary } from '../../Results/ReactionSummary';
import { ResultsControls } from '../../Results/ResultsControls';
import { WordCloud } from '../../Results/WordCloud';
import { SurveyView } from '../SurveyView';
import { useSelectedQuestionInfo } from './useSelectedQuestionInfo';
import { useLoadResultsViewData } from './useLoadResultsViewData';
import { useMergedSurveyViewExperiment } from '../../hooks/useMergedSurveyViewExperiment';

const AlertContainer = styled.div`
  width: 80%;
  margin: 0 auto ${tokens.spacer.x4} auto;
`;

const ResultWrapper = styled.div`
  padding: 0 0 ${tokens.spacer.x4} 0;
`;

const SurveyResultsViewContent = ({
  surveyId,
  questions,
  questionResults,
}: {
  surveyId: number;
  questions: SurveyQuestion[];
  questionResults: QuestionResults;
}) => {
  const { t } = useTranslationWithPrefix('feedback:results');

  const questionInfo = useSelectedQuestionInfo(surveyId, false);

  const isInMergedViewExperiment = useMergedSurveyViewExperiment();

  if (questionInfo === undefined || questionInfo === 'ALL') return null;

  const result = questionResults[questionInfo.uuid];

  return (
    <>
      {!isInMergedViewExperiment && (
        <ResultsControls totalAggregatedResults={result?.total} surveyId={surveyId} />
      )}

      {!isInMergedViewExperiment &&
        (questionInfo.viewMode === 'none' || result === null || result === undefined) && (
          <AlertContainer>
            <Alert kind="info">{t('none-view-mode')}</Alert>
          </AlertContainer>
        )}

      {result?.total === 0 && (
        <AlertContainer>
          <Alert kind="info">{t('empty-responses')}</Alert>
        </AlertContainer>
      )}

      {result !== undefined && result.total > 0 && (
        <ResultWrapper>
          {questionInfo.viewMode === 'wordcloud' && (
            <WordCloud width={800} height={500} words={'words' in result ? result.words : []} />
          )}
          {questionInfo.viewMode === 'net-promoter-score' && (
            <NPSView result={result as NpsResult} />
          )}
          {questionInfo.viewMode === 'bar-chart-with-table' && (
            <BarChartWithTable
              questions={questions}
              result={result as AggregatedResult}
              selectedQuestionUuid={questionInfo.uuid}
            />
          )}
          {questionInfo.viewMode === 'score-summary' && (
            <ReactionSummary
              reactionStyle={questionInfo.style}
              results={result as AggregatedResult}
              surveyId={surveyId}
            />
          )}
        </ResultWrapper>
      )}
    </>
  );
};

export const SurveyResultsView = (): JSX.Element => {
  const { isLoaded, questions, surveyId, questionResults } = useLoadResultsViewData();
  return (
    <SurveyView view="results" surveyId={surveyId} isLoaded={isLoaded}>
      {questions !== undefined && surveyId !== undefined && questionResults !== undefined && (
        <SurveyResultsViewContent
          questions={questions}
          surveyId={surveyId}
          questionResults={questionResults}
        />
      )}
    </SurveyView>
  );
};

export const SurveyResults = (): JSX.Element | null => {
  const { questions, surveyId, questionResults } = useLoadResultsViewData();
  return (
    <>
      {questions !== undefined && surveyId !== undefined && questionResults !== undefined && (
        <SurveyResultsViewContent
          questions={questions}
          surveyId={surveyId}
          questionResults={questionResults}
        />
      )}
    </>
  );
};
